import { FC, ReactNode } from 'react';
import {
  DataRoomMetrics,
  LAST_ACTIVE_PLACEHOLDER,
  lastActiveOf,
  LastActiveUnit,
  VIEWING_TIME_UNIT_LONG,
  viewingTimeParts,
} from 'shared';

import { ChevronDownIcon } from '../../../../assets/icons/index';
import { AppFormattedMessage } from '../../../../components/app-formatted-message/index';
import { StringKey } from '../../../../lang/index';

/**
 * The strip across the top of a data room's root — 20.4 section 5.
 *
 * Lifetime figures, not a window: the design draws no range selector here, and the one that does
 * live on the Activity tabs is a click away through the link at the end. The numbers come from the
 * same journal those tabs read, so the two cannot disagree about a room.
 */
// Plain words, like `formatViewingTime` beside them: the strip sets the unit in its own type and
// the catalogue has no entry for a bare "Mins".
const UNIT_LABEL: Record<LastActiveUnit, string> = {
  [LastActiveUnit.DAYS]: 'Days',
  [LastActiveUnit.HOURS]: 'Hours',
  [LastActiveUnit.JUST_NOW]: 'Now',
  [LastActiveUnit.MINUTES]: 'Mins',
};

const Cell: FC<{ children: ReactNode; label: StringKey }> = ({ children, label }) => (
  <div className="flex min-w-0 flex-1 flex-col items-center px-4 last:border-r-0 [&:not(:last-child)]:border-r [&:not(:last-child)]:border-gray-200">
    <div className="flex h-8 items-center justify-center gap-1">{children}</div>
    <span className="w-full truncate text-center text-label-sm font-medium leading-4 text-gray-600">
      <AppFormattedMessage id={label} />
    </span>
  </div>
);

const Value: FC<{ children: ReactNode }> = ({ children }) => (
  <span className="text-base font-semibold leading-8 text-[#181D27]">{children}</span>
);

const Unit: FC<{ children: ReactNode }> = ({ children }) => (
  <span className="text-label-sm font-medium leading-4 text-gray-500">{children}</span>
);

export type WorkspaceDataRoomStatsProps = {
  metrics: DataRoomMetrics;
  onOpenFile: (fileId: string, fileName: string) => void;
  onViewActivity: () => void;
};

export const WorkspaceDataRoomStats: FC<WorkspaceDataRoomStatsProps> = ({
  metrics,
  onOpenFile,
  onViewActivity,
}) => {
  const viewingTime = viewingTimeParts(metrics.viewingTimeSeconds * 1000);
  const lastUpdate = lastActiveOf(metrics.lastActivityAt);

  return (
    // The panel the strip sits in: a half-opacity gray-50 over whatever is behind it, a gray-100
    // hairline and 12 of radius, with the metric group's own 8 nested inside its 16 of padding.
    <div className="flex items-center gap-6 rounded-xl border border-gray-100 bg-gray-50/50 p-4">
      <div className="flex flex-1 rounded-lg border border-gray-200 py-2">
        <Cell label="VISITORS">
          <Value>{metrics.visitorCount}</Value>
        </Cell>
        <Cell label="VIEWS">
          <Value>{metrics.viewCount}</Value>
        </Cell>
        <Cell label="DOWNLOADS">
          <Value>{metrics.downloadCount}</Value>
        </Cell>
        <Cell label="VIEWING_TIME">
          <Value>{viewingTime.value}</Value>
          <Unit>{VIEWING_TIME_UNIT_LONG[viewingTime.unit]}</Unit>
        </Cell>
        <Cell label="MOST_VIEWED">
          {metrics.mostViewedFile ? (
            <button
              className="w-[88px] truncate text-sm font-[550] leading-5 text-brand-700 underline"
              onClick={() =>
                metrics.mostViewedFile &&
                onOpenFile(metrics.mostViewedFile.id, metrics.mostViewedFile.name)
              }
              type="button"
            >
              {metrics.mostViewedFile.name}
            </button>
          ) : (
            <Value>{LAST_ACTIVE_PLACEHOLDER}</Value>
          )}
        </Cell>
        <Cell label="LAST_UPDATE">
          {/* Reuses the relative clock the rest of the room reads by; past a week it is a date,
              which has no unit beside it. */}
          {lastUpdate && 'count' in lastUpdate ? (
            <>
              <Value>{lastUpdate.count}</Value>
              <Unit>{UNIT_LABEL[lastUpdate.unit]}</Unit>
            </>
          ) : (
            <Value>{lastUpdate?.date ?? LAST_ACTIVE_PLACEHOLDER}</Value>
          )}
        </Cell>
      </div>

      <button
        className="flex shrink-0 items-center gap-2 text-sm font-medium leading-4 text-brand-700"
        onClick={onViewActivity}
        type="button"
      >
        <AppFormattedMessage id="VIEW_ACTIVITY" />
        <ChevronDownIcon className="size-4 -rotate-90" />
      </button>
    </div>
  );
};
